import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useCartStore } from '@/store/cartStore';
import { resolveFileUrl } from '@/utils/fileUrl';
import { posApi } from '@/services/api';
import {
  publishCustomerDisplay,
  subscribeCustomerDisplaySyncRequests,
} from '@/utils/customerDisplayChannel';

export interface CustomerDisplayProfile {
  company_name?: string;
  company_logo?: string;
  currency?: string;
  country?: string;
  ui_language?: string;
}

export function broadcastCustomerDisplay(payload: Record<string, unknown>) {
  publishCustomerDisplay(payload);
}

export function useCustomerDisplayBridge(profile?: CustomerDisplayProfile) {
  const items = useCartStore((s) => s.items);
  const discount = useCartStore((s) => s.discount);
  const tax = useCartStore((s) => s.tax);
  const getSubtotal = useCartStore((s) => s.getSubtotal);
  const getNetTotal = useCartStore((s) => s.getNetTotal);
  const getDiscountAmount = useCartStore((s) => s.getDiscountAmount);
  const getTaxAmount = useCartStore((s) => s.getTaxAmount);

  const profileRef = useRef(profile);
  profileRef.current = profile;

  const displayItems = useMemo(
    () =>
      items.map((line) => ({
        name: line.variant_name ? `${line.item_name} — ${line.variant_name}` : line.item_name,
        qty: line.quantity,
        amount: line.amount,
      })),
    [items],
  );

  const buildPayload = useCallback(() => {
    const p = profileRef.current;
    return {
      type: 'cart' as const,
      data: {
        companyName: p?.company_name,
        logoUrl: resolveFileUrl(p?.company_logo),
        currency: p?.currency,
        country: p?.country,
        uiLanguage: p?.ui_language,
        items: displayItems,
        subtotal: getSubtotal(),
        discount: getDiscountAmount(),
        tax: getTaxAmount(),
        total: getNetTotal(),
        status: items.length ? 'active' : 'idle',
      },
    };
  }, [
    displayItems,
    getDiscountAmount,
    getNetTotal,
    getSubtotal,
    getTaxAmount,
    items.length,
  ]);

  const broadcastRef = useRef(buildPayload);
  broadcastRef.current = buildPayload;

  useEffect(() => {
    const payload = buildPayload();
    publishCustomerDisplay(payload);
    const timer = window.setTimeout(() => {
      void posApi.pushCustomerDisplaySnapshot(payload.data as Record<string, unknown>).catch(() => {});
    }, 400);
    return () => window.clearTimeout(timer);
  }, [buildPayload, discount, tax]);

  useEffect(() => {
    return subscribeCustomerDisplaySyncRequests(() => {
      publishCustomerDisplay(broadcastRef.current());
    });
  }, []);
}

export function broadcastThankYou(message?: string) {
  const msg = message || 'Thank you!';
  publishCustomerDisplay({ type: 'thankyou', message: msg });
  void posApi.pushCustomerDisplaySnapshot({
    status: 'thankyou',
    thankYouMessage: msg,
    items: [],
    total: 0,
    subtotal: 0,
    discount: 0,
    tax: 0,
  }).catch(() => {});
}
